import { RefObject, useCallback, useEffect, useState } from 'react';

export type SetElementKind = 'logo' | 'title' | 'subheader' | 'body' | 'category';

export type SelectedOverlayElement =
  | { kind: SetElementKind; id?: undefined }
  | { kind: 'design'; id: string };

/**
 * One selection per overlay editor, for set elements and design elements
 * alike. Click selects (the ElementFrame calls `select`), a pointer-down
 * anywhere else inside the editor deselects, Escape deselects — or, while
 * the cursor is in a text box, leaves typing and keeps the box selected.
 * Hover does nothing. There is no "mouse wandered away" timeout.
 *
 * `rootRef` is the editor's canvas: a click inside another card's element
 * must deselect this one, and a click inside a portaled picker (marked
 * `data-overlay-popover`) must not.
 */
export function useOverlaySelection(rootRef: RefObject<HTMLElement | null>, enabled = true) {
  const [selected, setSelected] = useState<SelectedOverlayElement | null>(null);

  const select = useCallback((next: SelectedOverlayElement) => setSelected(next), []);
  const clear = useCallback(() => setSelected(null), []);
  const isSelected = useCallback(
    (kind: SelectedOverlayElement['kind'], id?: string) =>
      !!selected && selected.kind === kind && (kind !== 'design' || selected.id === id),
    [selected],
  );

  useEffect(() => {
    if (!selected || !enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('[data-overlay-popover]')) return;
      const root = rootRef.current;
      // A press anywhere outside the text being edited commits it: blur NOW,
      // while the box is still contentEditable. Deselecting turns that off in
      // the next render, after which the browser fires no blur at all — the
      // focus flag stayed true, the placeholder never came back and the edit
      // waited for unmount to save (2026-10-07). Toolbar presses keep the caret.
      const ae = document.activeElement as HTMLElement | null;
      if (ae && ae.isContentEditable && root?.contains(ae) && !ae.contains(t) && !t.closest('[data-overlay-toolbar]')) {
        ae.blur();
      }
      if (root && root.contains(t) && t.closest('[data-overlay-element]')) return;
      setSelected(null);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // An open picker owns Escape (it closes itself).
      if (document.querySelector('[data-overlay-popover]')) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && ae.isContentEditable && rootRef.current?.contains(ae)) {
        ae.blur();
        return;
      }
      setSelected(null);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selected, enabled, rootRef]);

  // Deselected while typing (click-away, Escape path above excluded): drop
  // the caret too, so the box does not keep editing without its frame.
  useEffect(() => {
    if (selected) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && ae.isContentEditable && rootRef.current?.contains(ae)) ae.blur();
  }, [selected, rootRef]);

  return { selected, select, clear, isSelected };
}
